import React, { useContext } from 'react';
import { ETAPES, PlanningEtapesContext, motifEtape, premiereAFaire } from './PlanningEtapes';
import BoutonAide from './aide/BoutonAide';
import './PlanningApp.css';

/**
 * Topbar du module Planning annuel — source UNIQUE (lot 5.4).
 *
 * Elle était écrite deux fois : inlinée dans `PlanningApp.js` et recopiée dans
 * `setup/SetupHeader.js`. Deux barres pour un seul module, donc deux occasions de
 * diverger — exactement ce que la factorisation des catégories d'événements a
 * corrigé au lot 5.2. La fusion des deux écrans est le moment de n'en garder qu'une.
 *
 * LOT PA.1 (revue ergonomique, maquette « accueil 2 » du 24/10) — les quatre onglets
 * d'avant (Calendrier · Paramétrage · Export PDF Calendrier · Export PDF Événements)
 * deviennent les ÉTAPES de la vignette « Planning annuel », dans une barre blanche
 * sous la barre bleue, comme une vignette de suivi du Stage :
 *   Préparer 1 Configuration · 2 Types d'événements · 3 Formations
 *   | Construire 4 Calendrier | Diffuser Export PDF Calendrier · Export PDF Événements
 * La barre bleue ne porte plus que « Accueil » et l'onglet de la vignette ouverte
 * (« Planning annuel 2025-2026 × », × = retour à l'accueil). L'ordre des exports
 * reste celui que le lot 5.7 avait rétabli.
 *
 * Le numéro d'une étape dit son état (vert coché : faite · orange : la première à
 * faire), d'après `GET /api/planning/accueil` ; son infobulle dit ce qui manque.
 *
 * Props :
 *   sousTitre    : string  — « département · année », déjà composé par l'appelant
 *   onglet       : 'accueil' | 'etape1' | 'etape2' | 'etape3' | 'calendrier' | 'export' | 'exportevt'
 *   onChangeOnglet(cle)
 *   calendrierPret : bool  — false tant qu'aucune configuration n'existe : le
 *                    Calendrier et les exports sont alors grisés AVEC LEUR MOTIF,
 *                    plutôt qu'ouverts sur un écran vide. Par défaut : le contexte.
 *   children     : les actions propres à l'écran affiché (zone de droite)
 */
function PlanningTopbar({ sousTitre, onglet, onChangeOnglet, calendrierPret, children }) {
    const { etat, anneeLibelle, configPrete, bandeau } = useContext(PlanningEtapesContext);
    const pret = calendrierPret ?? configPrete;
    const ouverte = onglet !== 'accueil';
    const aFaire = premiereAFaire(etat);

    return (
        <>
            {/* Lot PA.4 : `bandeau` = le repère du volet d'aide, qui s'ouvre juste dessous */}
            <div className="pl-topbar" ref={bandeau}>
                <i className="fa-solid fa-calendar-days pl-topbar-icon" />
                <div className="pl-topbar-id">
                    {/* Le titre affiché est le nom du MODULE, pas `titre_planning` :
                        écart E1, assumé et écrit en spec. Le titre personnalisé reste
                        saisi au Paramétrage et sert aux exports PDF. */}
                    <div className="pl-topbar-title">Planning annuel</div>
                    <div className="pl-topbar-sub">{sousTitre}</div>
                </div>

                <div className="pl-topbar-tabs">
                    <button
                        type="button"
                        className={'pl-topbar-tab' + (!ouverte ? ' active' : '')}
                        onClick={() => onChangeOnglet('accueil')}
                    >
                        <i className="fa-solid fa-house" /> Accueil
                    </button>
                    {ouverte && (
                        <button type="button" className="pl-topbar-tab active">
                            <i className="fa-solid fa-calendar-days" /> Planning annuel {anneeLibelle}
                            <span
                                className="pl-tab-x"
                                title="Revenir à l'accueil"
                                role="button"
                                onClick={(e) => { e.stopPropagation(); onChangeOnglet('accueil'); }}
                            >
                                ×
                            </span>
                        </button>
                    )}
                </div>

                {/* Le « ? » de l'aide (lot PA.4) : le dernier de la zone de droite, sur tous les écrans du module */}
                <div className="pl-topbar-actions">{children}<BoutonAide /></div>
            </div>

            {ouverte && (
                <div className="pl-etapes">
                    {ETAPES.map((e) => {
                        const fait = e.n && etat?.etapes?.[e.n]?.faite;
                        const etatNum = fait ? 'ok' : (e.cle === aFaire && e.n ? 'afaire' : '');
                        const bloquee = e.exigeConfig && !pret;
                        return (
                            <React.Fragment key={e.cle}>
                                {e.groupe && (
                                    <span className={'pl-etapes-groupe' + (e.groupe !== 'Préparer' ? ' trait' : '')}>
                                        {e.groupe}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className={'pl-etape' + (e.cle === onglet ? ' active' : '')}
                                    onClick={() => onChangeOnglet(e.cle)}
                                    disabled={bloquee}
                                    title={bloquee
                                        ? 'Enregistrez d\'abord les formations (étape 3) pour ouvrir cet écran'
                                        : (e.n ? motifEtape(e.n, etat) : undefined)}
                                >
                                    {e.n && (
                                        <span className={'pl-etape-num ' + etatNum}>
                                            {fait ? <i className="fa-solid fa-check" /> : e.n}
                                        </span>
                                    )}
                                    <i className={'fa-solid ' + e.ic} /> {e.lib}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </>
    );
}

export default PlanningTopbar;
